import { Component, Inject, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { Appointment, Slot, Bundle } from 'fhir/r4';
import { FhirResourceService } from '../../shared/fhir-resource.service';
import { HttpClient, HttpContext } from '@angular/common/http';
import { backendEndPointToken } from '../../app.config';
import { UtilityService } from '../../shared/utility.service';
import { Observable, forkJoin, EMPTY, of } from 'rxjs';
import { expand, map, reduce, catchError } from 'rxjs/operators';
import { LoadingUIEnabled } from '../../loading.interceptor';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ErrorService } from '../../shared/error.service';

export interface ShiftProposedChange {
  appointment: Appointment;
  patientName: string;
  originalStart: Date;
  originalEnd: Date;
  proposedStart: Date;
  proposedEnd: Date;
  hasConflict: boolean;
}

@Component({
  selector: 'app-shift-cascade-dialog',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, 
    MatIconModule, MatInputModule, MatTableModule, MatProgressSpinnerModule, MatSelectModule
  ],
  templateUrl: './shift-cascade-dialog.component.html',
  styleUrls: ['./shift-cascade-dialog.component.scss']
})
export class ShiftCascadeDialogComponent implements OnInit {
  fb = inject(FormBuilder);
  fhirResourceService = inject(FhirResourceService);
  http = inject(HttpClient);
  backendUrl = inject(backendEndPointToken);
  utilityService = inject(UtilityService);
  errorService = inject(ErrorService);

  shiftForm: FormGroup;
  loading = true;
  saving = false;
  
  targetAppointment: Appointment;
  existingAppointments: Appointment[] = [];
  workingSlots: Slot[] = [];
  shiftBlocks: {startMs: number, endMs: number}[] = [];
  
  proposedChanges: ShiftProposedChange[] = [];
  displayedColumns = ['patient', 'originalTime', 'proposedTime', 'status'];
  
  constructor(
    public dialogRef: MatDialogRef<ShiftCascadeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { appointment: Appointment }
  ) {
    this.targetAppointment = data.appointment;
    this.shiftForm = this.fb.group({
      shiftMinutes: [30, [Validators.required, Validators.min(0)]],
      direction: ['forward']
    });
  }

  ngOnInit() {
    this.fetchSchedule();
    this.shiftForm.valueChanges.subscribe(() => {
      this.calculateCascade();
    });
  }

  getPatientName(appointment: Appointment): string {
    const patientRef = appointment.participant?.find(p => p.actor?.reference?.startsWith('Patient/'));
    return patientRef?.actor?.display || patientRef?.actor?.reference || 'Unknown Patient';
  }

  fetchSchedule() {
    this.loading = true;
    const practitionerRef = this.targetAppointment.participant?.find(p => p.actor?.reference?.startsWith('Practitioner/'))?.actor?.reference;
    const practitionerId = practitionerRef?.replace('Practitioner/', '');
    
    if (!practitionerId || !this.targetAppointment.start) {
      this.errorService.openandCloseError('Missing practitioner or date info on appointment.');
      this.dialogRef.close();
      return;
    }

    const apptDate = new Date(this.targetAppointment.start);
    const dateStr = apptDate.toISOString().split('T')[0];

    forkJoin({
      appointments: this.fetchAppointmentsForDay(practitionerId, dateStr),
      slots: this.fetchSlotsForDay(practitionerId, dateStr)
    }).subscribe({
      next: (res) => {
        // Sort appointments chronologically
        this.existingAppointments = res.appointments
            .filter(a => a.status === 'booked' || a.status === 'arrived' || a.status === 'pending')
            .sort((a, b) => new Date(a.start!).getTime() - new Date(b.start!).getTime());
        this.workingSlots = res.slots;
        this.calculateShiftBlocks();
        this.calculateCascade();
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.errorService.openandCloseError('Failed to load schedule for cascade preview.');
        this.dialogRef.close();
      }
    });
  }

  calculateShiftBlocks() {
    const sortedSlots = [...this.workingSlots].filter(s => s.start && s.end).sort((a, b) => new Date(a.start!).getTime() - new Date(b.start!).getTime());
    if (sortedSlots.length === 0) {
      this.shiftBlocks = [];
      return;
    }
    
    let currentBlock = { 
      startMs: new Date(sortedSlots[0].start!).getTime(), 
      endMs: new Date(sortedSlots[0].end!).getTime() 
    };
    const workingBlocks = [];
    
    for (let i = 1; i < sortedSlots.length; i++) {
      const slot = sortedSlots[i];
      const startMs = new Date(slot.start!).getTime();
      const endMs = new Date(slot.end!).getTime();
      
      if (startMs <= currentBlock.endMs) {
        currentBlock.endMs = Math.max(currentBlock.endMs, endMs);
      } else {
        workingBlocks.push(currentBlock);
        currentBlock = { startMs, endMs };
      }
    }
    workingBlocks.push(currentBlock);
    this.shiftBlocks = workingBlocks;
  }

  calculateCascade() {
    let shiftMins = this.shiftForm.value.shiftMinutes;
    const direction = this.shiftForm.value.direction;
    if (!shiftMins || shiftMins === 0) {
      this.proposedChanges = [];
      return;
    }

    if (direction === 'backward') {
      shiftMins = -Math.abs(shiftMins);
    } else {
      shiftMins = Math.abs(shiftMins);
    }

    const changes: ShiftProposedChange[] = [];
    let currentShiftAmountMs = shiftMins * 60000;
    
    // Find index of the target appointment
    const targetIndex = this.existingAppointments.findIndex(a => a.id === this.targetAppointment.id);
    if (targetIndex === -1) return;

    let previousPushedEndMs = 0;

    for (let i = targetIndex; i < this.existingAppointments.length; i++) {
      const appt = this.existingAppointments[i];
      if (!appt.start || !appt.end) continue;
      
      const originalStartMs = new Date(appt.start).getTime();
      const originalEndMs = new Date(appt.end).getTime();
      const durationMs = originalEndMs - originalStartMs;
      
      let proposedStartMs = originalStartMs;
      let isOverlapConflict = false;
      
      if (i === targetIndex) {
        proposedStartMs = originalStartMs + currentShiftAmountMs;
        
        // If shifting backwards, check for collision with previous appointment
        if (shiftMins < 0 && targetIndex > 0) {
            const prevAppt = this.existingAppointments[targetIndex - 1];
            const prevEndMs = new Date(prevAppt.end!).getTime();
            if (proposedStartMs < prevEndMs) {
                isOverlapConflict = true;
            }
        }
      } else {
        // If the previous appointment got pushed into this one
        if (previousPushedEndMs > originalStartMs) {
          proposedStartMs = previousPushedEndMs;
        } else {
          // No collision with previous, ripple stops here!
          break; 
        }
      }
      
      // We jump over breaks if shifting FORWARD
      if (shiftMins > 0) {
        proposedStartMs = this.adjustToNextAvailableShiftBlock(proposedStartMs);
      }
      const proposedEndMs = proposedStartMs + durationMs;
      
      // Check if it ends outside of shift hours
      const endsWithinShift = this.shiftBlocks.some(block => proposedStartMs >= block.startMs && proposedEndMs <= block.endMs);
      
      changes.push({
        appointment: appt,
        patientName: this.getPatientName(appt),
        originalStart: new Date(originalStartMs),
        originalEnd: new Date(originalEndMs),
        proposedStart: new Date(proposedStartMs),
        proposedEnd: new Date(proposedEndMs),
        hasConflict: !endsWithinShift || isOverlapConflict
      });
      
      previousPushedEndMs = proposedEndMs;
    }
    
    this.proposedChanges = [...changes];
  }

  adjustToNextAvailableShiftBlock(timeMs: number): number {
    if (this.shiftBlocks.length === 0) return timeMs; // no bounds known
    
    // If it's strictly before the first block, move it to the start of the first block
    if (timeMs < this.shiftBlocks[0].startMs) {
      return this.shiftBlocks[0].startMs;
    }

    for (let i = 0; i < this.shiftBlocks.length; i++) {
      const block = this.shiftBlocks[i];
      if (timeMs >= block.startMs && timeMs < block.endMs) {
        return timeMs; // Inside a valid block
      }
      if (i < this.shiftBlocks.length - 1) {
        const nextBlock = this.shiftBlocks[i+1];
        if (timeMs >= block.endMs && timeMs < nextBlock.startMs) {
          // It fell in a break! Push it to the start of the next block.
          return nextBlock.startMs;
        }
      }
    }
    return timeMs; // After last block, will be caught by hasConflict check
  }

  hasAnyConflicts(): boolean {
    return this.proposedChanges.some(c => c.hasConflict);
  }

  submitTransaction() {
    if (this.proposedChanges.length === 0) return;
    
    this.saving = true;
    
    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: []
    };

    this.proposedChanges.forEach(change => {
      const updatedAppt = JSON.parse(JSON.stringify(change.appointment)) as any;
      updatedAppt.start = change.proposedStart.toISOString();
      updatedAppt.end = change.proposedEnd.toISOString();
      
      // Strip custom UI properties added by toAppointmentTableRow
      delete updatedAppt.date;
      delete updatedAppt.displayType;
      delete updatedAppt.serviceCategoryDisplay;
      delete updatedAppt.specialtyDisplay;
      delete updatedAppt.patientRef;
      
      bundle.entry!.push({
        resource: updatedAppt,
        request: {
          method: 'PUT',
          url: `Appointment/${updatedAppt.id}`
        }
      });
    });

    this.fhirResourceService.postBundle(bundle).subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        console.error(err);
        this.saving = false;
        this.errorService.openandCloseError('Transaction failed.');
      }
    });
  }

  // Same fetch logic from MakeAppointmentsComponent
  fetchAppointmentsForDay(practitionerId: string, dateStr: string): Observable<Appointment[]> {
    const actor = encodeURIComponent(`Practitioner/${practitionerId}`);
    const startGe = encodeURIComponent(`${dateStr}T00:00:00Z`);
    const startLe = encodeURIComponent(`${dateStr}T23:59:59Z`);
    const status = encodeURIComponent('booked,arrived,fulfilled,in-progress,tentative,pending');
    const queries = `actor=${actor}&date=ge${startGe}&date=le${startLe}&status=${status}`;
    const url = `${this.backendUrl}/Appointment?${queries}`;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    
    return this.http.get<Bundle<any>>(url, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 50) return EMPTY;
        const nextLink = (bundle?.link ?? []).find((link) => String(link?.relation ?? '').toLowerCase() === 'next')?.url;
        const nextUrl = this.utilityService.normalizeNextLinkUrl(nextLink ?? null, url);
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => (bundle?.entry ?? []).map(entry => entry.resource).filter(Boolean)),
      reduce((acc, curr) => acc.concat(curr), [] as any[]),
      map(resources => resources.filter((r: any) => r.resourceType === 'Appointment') as Appointment[]),
      catchError(() => of([]))
    );
  }

  fetchSlotsForDay(practitionerId: string, dateStr: string): Observable<Slot[]> {
    const actor = encodeURIComponent(`Practitioner/${practitionerId}`);
    const startGe = encodeURIComponent(`${dateStr}T00:00:00Z`);
    const startLe = encodeURIComponent(`${dateStr}T23:59:59Z`);
    const queries = `schedule.actor=${actor}&start=ge${startGe}&start=le${startLe}&status=free`;
    const url = `${this.backendUrl}/Slot?${queries}`;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    
    return this.http.get<Bundle<any>>(url, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 50) return EMPTY;
        const nextLink = (bundle?.link ?? []).find((link) => String(link?.relation ?? '').toLowerCase() === 'next')?.url;
        const nextUrl = this.utilityService.normalizeNextLinkUrl(nextLink ?? null, url);
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => (bundle?.entry ?? []).map(entry => entry.resource).filter(Boolean)),
      reduce((acc, curr) => acc.concat(curr), [] as any[]),
      map(resources => resources.filter((r: any) => r.resourceType === 'Slot') as Slot[]),
      catchError(() => of([]))
    );
  }
}
